import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Import all your media here
import Anterdhvani1 from '../../assets/Anterdhvani.jpeg';
import Anterdhvani2 from '../../assets/Anterdhvani2.jpeg'; 
import Anterdhvani3 from '../../assets/Anterdhvani3.jpeg';
import Anterdhvani4 from '../../assets/Anterdhvani4.jpeg';
import Anterdhvani5Video from '../../assets/Anterdhvani5.mp4';

const items = [
  { src: Anterdhvani5Video, type: 'video' },
  { src: Anterdhvani4 },
  { src: Anterdhvani3 },
  { src: Anterdhvani2 },
  { src: Anterdhvani1 },
];

const DESKTOP_VISIBLE = 3;

function useVisibleSlideCount() {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCount(media.matches ? DESKTOP_VISIBLE : 1);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return count;
}

const AnterdhvaniCard = ({ item, index }) => (
  <article className="flex h-full w-full max-w-[350px] overflow-hidden rounded-2xl bg-white shadow-lg">
    {item.type === 'video' ? (
      <video
        src={item.src}
        controls
        className="h-auto w-full rounded-2xl object-cover"
      />
    ) : (
      <img
        src={item.src}
        alt={`Anterdhvani ${index + 1}`}
        className="h-auto w-full rounded-2xl object-cover"
      />
    )}
  </article>
);

const HomeSectionAnterdhvani = () => {
  const visibleCount = useVisibleSlideCount();
  const total = items.length;
  const maxIndex = Math.max(0, total - visibleCount);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(Math.max(0, Math.min(index, maxIndex)));
    },
    [maxIndex],
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  const useCarousel = total > visibleCount;
  const trackWidthPercent = (total / visibleCount) * 100;
  const slideShareOfTrack = 100 / total;

  return (
    <section className="bg-bg-light px-6 py-14 md:px-12">
      <div className="mx-auto max-w-6xl">

        {/* Title and Description */}
        <div className="mb-10 text-center md:mb-14">
          <h2 className="mb-4 text-3xl font-bold uppercase text-accent md:text-4xl">
            Anterdhvani
          </h2>
          <p className="text-base leading-relaxed text-gray-700 md:text-lg">
            Lines which emerge from the Soul of a Himmatwaali
          </p>
        </div>

        {useCarousel ? (
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${trackWidthPercent}%`,
                transform: `translateX(-${activeIndex * slideShareOfTrack}%)`,
              }}
            >
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-center px-2 sm:px-3"
                  style={{ width: `${slideShareOfTrack}%` }}
                >
                  <AnterdhvaniCard item={item} index={index} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-stretch justify-center gap-8 md:gap-12">
            {items.map((item, index) => (
              <div key={index} className="flex w-full max-w-[350px] justify-center">
                <AnterdhvaniCard item={item} index={index} />
              </div>
            ))}
          </div>
        )}

        {useCarousel && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={goPrev}
              disabled={activeIndex === 0}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous Anterdhvani"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Anterdhvani slides"
            >
              {Array.from({ length: maxIndex + 1 }, (_, pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  role="tab"
                  aria-selected={pageIndex === activeIndex}
                  aria-label={`Go to slide group ${pageIndex + 1}`}
                  onClick={() => goTo(pageIndex)}
                  className={`h-2.5 rounded-full transition-all ${
                    pageIndex === activeIndex
                      ? 'w-8 bg-accent'
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={activeIndex >= maxIndex}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next Anterdhvani"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </div>
        )}

      </div>
    </section>
  );
};

export default HomeSectionAnterdhvani;
